import React, { useMemo } from 'react';
import { StyleSheet, Pressable, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Badge from '@/components/Typography/Badge';
import PropTypes from 'prop-types';
import { Image } from 'expo-image';

const ListItem = ({ 
  imageUrl,
  leading,
  iconName,
  iconSize = 24,
  iconColor,
  iconBackgroundColor,
  title,
  subtitle,
  badges = [],
  onPress,
  isLastItem = false,
  isBordered = false,
  isRounded = false,
  isSquare = false,
  numberOfLines = 2,
  isXS,
}) => {
  const { colors } = useTheme();
  const styles = useMemo(() => createListItemStyles(colors, isXS, isLastItem, isBordered, isRounded, isSquare), [colors, isXS, isLastItem, isBordered, isRounded, isSquare]);

  return (
    <Pressable 
      style={({ hovered }) => [
        styles.container,
        hovered && { backgroundColor: colors.solid_color_tertiary }
      ]}
      onPress={onPress}
    >
      {leading}
      {
        (imageUrl || iconName) && (
        <View style={styles.iconContainer}>
          {imageUrl ? (
            <Image source={{ uri: imageUrl }} style={styles.icon} contentFit="cover" />
          ) : (
            <View style={[styles.icon, {
              backgroundColor: iconBackgroundColor || colors.solid_color_secondary,
              justifyContent: 'center',
              alignItems: 'center'
            }]}>
              <Icon 
                name={iconName} 
                size={iconSize} 
                color={iconColor || colors.text_color_secondary} 
              />
            </View>
          )}
        </View>
      )}
      <View style={styles.content}>
        <View style={styles.titleRow}>
          <Text color='primary' weight='medium' size='l' numberOfLines={numberOfLines}>{title}</Text>
          <View style={styles.badgeContainer}>
            {badges.map((badgeProps, index) => (
              <Badge key={index} {...badgeProps} />
            ))}
          </View>
        </View>
        <View style={styles.subtitleRow}>
          {subtitle}
        </View>
      </View>
    </Pressable>
  );
};

const createListItemStyles = (colors, isXS, isLastItem, isBordered, isRounded, isSquare) => StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    gap: 12,
    borderBottomWidth: (isBordered && !isLastItem) ? 0.5 : 0,
    borderBottomColor: colors.border_color_secondary,
  },
  iconContainer: {
    width: 44,
    height: 44,
    borderRadius: isRounded ? 22 : isSquare ? 0 : 8,
    backgroundColor: '#fff',
  },
  icon: {
    width: '100%',
    height: '100%',
    borderRadius: isRounded ? 22 : isSquare ? 0 : 8,
    backgroundColor: colors.solid_color_secondary,
  },
  content: {
    flex: 1,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: isXS ? 2 : 4,
  },
  subtitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  badgeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
});

ListItem.propTypes = {
  numberOfLines: PropTypes.number,
  imageUrl: PropTypes.string,
  leading: PropTypes.node,
  iconName: PropTypes.string,
  iconSize: PropTypes.number,
  iconColor: PropTypes.string,
  iconBackgroundColor: PropTypes.string,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.node,
  badges: PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.string,
    icon: PropTypes.string,
    isPlus: PropTypes.bool,
    isLarge: PropTypes.bool,
    onPress: PropTypes.func,
    style: PropTypes.object,
    iconColor: PropTypes.string,
    textColor: PropTypes.string,
    solid: PropTypes.bool,
    backgroundColor: PropTypes.string
  })),
  onPress: PropTypes.func,
  isXS: PropTypes.bool,
  isRounded: PropTypes.bool,
};

export default React.memo(ListItem); 